import { React, useEffect, useState } from "react"
import AsyncSelect from "react-select/async"
import { Controller, useForm } from "react-hook-form"
import { Col, Input, Form, Button, Label, Row, FormFeedback } from "reactstrap"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import Flatpickr from "react-flatpickr"
import { provideOptionsToSelect, getISODateUTC, checkFieldValidity } from "@lib/util"
import {
  addEventInPersonHistory,
  editEventInPersonHistory,
  getPersonInteractions,
  getLoggedInUser
} from "@lib/api"
import { EVENT_ID_RANGES, UNKNOWN_PARTNER } from "@lib/constants"
import SelectShowroomListbox from "../../components/SelectShowroomListbox"

const EventForm = (props) => {
  const [user, setUser] = useState({})
  const actionName = props.eventToEdit ? "Edit" : "Add"
  const today = getISODateUTC().split("Z")[0]
  const MySwal = withReactContent(Swal)
  // eventReasonId in this range are displayed
  const availableEvents = {
    min: props.eventIdRange?.min || EVENT_ID_RANGES.events.start,
    max: props.eventIdRange?.max || 999
  }

  // default form values
  let defaultValues = {}
  if (props.eventToEdit) {
    defaultValues = props.eventToEdit
  } else {
    defaultValues = {
      eventReason: null,
      description: "",
      partner: null,
      date: today
    }
  }
  const {
    control,
    setError,
    reset,
    handleSubmit,
    formState: { errors, isDirty }
  } = useForm({ defaultValues })

  useEffect(() => {
    // get logged in user info
    getLoggedInUser().then((user) => setUser(user))
  }, [])

  const onSubmit = async (data) => {
    let error = false
    const optionalFields = ["currency", "amount", "partner", "showroomId", "date"]
    for (const key in data) {
      if (optionalFields.includes(key)) continue

      if (!checkFieldValidity(data[key])) {
        setError(key, {
          type: "manual",
          message: `'${key}' is required`
        })
        error = true
      }
    }

    if (!user.collaboratorId || !user.internalCompanyId || !user.showroomId) {
      MySwal.fire({
        title: "Error",
        allowOutsideClick: false,
        html: `
        <div>
          <p>Your account is missing the following information :</p>
          ${!user.collaboratorId ? "<p>collaboratorId</p>" : ""}
          ${!user.internalCompanyId ? "<p>internalCompanyId</p>" : ""}
          ${!user.showroomId ? "<p>showroomId</p>" : ""}
        </div>`,
        icon: "error"
      })
      return
    }
    if (!error) {
      try {
        // get the first [0] interaction between a person and a specified company
        const interactions = await getPersonInteractions(props.personId, user.internalCompanyId)
        const firstInteractionId = interactions[0].id
        // cleanup data
        const cleanData = {
          // trick to conditionally add a key to an object
          ...(data.amount && { amount: data.amount.toString().replace(/\'/g, "") }),
          ...(data.currency && { currencyID: data.currency.value }),
          ...(props.eventToEdit && { id: props.eventToEdit.id }),
          interactionId: firstInteractionId,
          collaboratorId: user.collaboratorId,
          showroomId: data.showroomId || user.showroomId,
          description: data.description,
          eventReasonId: data.eventReason.value,
          partnerId: data.partner?.value,
          // weird formatting with flatpickr
          on: data.date.length === 1 ? getISODateUTC(new Date(data.date)) : today
        }
        if (props.eventToEdit) {
          await editEventInPersonHistory(cleanData)
        } else {
          reset()
          await addEventInPersonHistory(cleanData)
        }
        MySwal.fire({
          title: "Success",
          text: "Event informations successfully posted",
          customClass: {
            input: "mx-3",
            confirmButton: "btn btn-success mx-1"
          },
          buttonsStyling: false,
          timer: 3000,
          timerProgressBar: true,
          confirmButtonText: "OK"
        })
        // refresh timeline if provided
        if (props.onSubmit) props.onSubmit()
      } catch (err) {
        console.error(err)
        MySwal.fire({
          title: "Error",
          text: err.message,
          icon: "error"
        })
      }
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-3">
        <h1>{actionName} an Event</h1>
      </div>
      <Row className="mb-1">
        <Label sm="3" for="eventReason">
          Event type *
        </Label>
        <Col sm="9">
          <Controller
            name="eventReason"
            id="eventReason"
            control={control}
            render={({ field }) => (
              <>
                <AsyncSelect
                  {...field}
                  isClearable={false}
                  isSearchable={false}
                  className="react-select"
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  loadOptions={async () => {
                    const options = await provideOptionsToSelect({
                      name: "Events/Reasons",
                      apiEndpoint: "visits"
                    })
                    return options.filter(
                      (option) =>
                        option.value > availableEvents.min && option.value < availableEvents.max
                    )
                  }}
                />
                {errors.eventReason && (
                  <small style={{ color: "red" }}>{errors.eventReason.message}</small>
                )}
              </>
            )}
          />
        </Col>
      </Row>

      <Row className="mb-1">
        <Col sm="3">
          <Label for="description">Event description *</Label>
        </Col>
        <Col sm="9">
          <Controller
            id="description"
            name="description"
            control={control}
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  type="textarea"
                  rows="4"
                  placeholder="Requests, visits, informations, etc..."
                  invalid={errors.description && true}
                />
                {errors.description && <FormFeedback>{errors.description.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>

      <Row className="mb-1">
        <Label sm="3" for="description">
          Event date *
        </Label>
        <Col sm="9">
          <Controller
            name="date"
            id="date"
            control={control}
            render={({ field }) => (
              <Flatpickr
                {...field}
                className="form-control"
                placeholder={today}
                options={{
                  dateFormat: "Y-m-d H:i",
                  locale: {
                    firstDayOfWeek: 1
                  },
                  maxDate: today,
                  enableTime: true,
                  time_24hr: true
                }}
              />
            )}
          />
        </Col>
      </Row>

      <Row className="mb-1">
        <Label sm="3" for="partner">
          Sent by (partner)
        </Label>
        <Col sm="9">
          <Controller
            name="partner"
            id="partner"
            control={control}
            render={({ field }) => (
              <>
                <AsyncSelect
                  {...field}
                  isClearable={true}
                  isSearchable={true}
                  className="react-select"
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  loadOptions={(inputSearchValue) =>
                    provideOptionsToSelect({
                      name: `Partners?filters[entityId]=gt_${UNKNOWN_PARTNER.id}&search=${inputSearchValue}`,
                      apiEndpoint: "crm"
                    })
                  }
                />
              </>
            )}
          />
        </Col>
      </Row>

      <hr className="my-3" />

      <Row>
        <Col>
          <SelectShowroomListbox user={user} control={control} />
        </Col>
        <Col className="align-self-center text-end">
          <Button color="success" type="submit" disabled={!isDirty}>
            {actionName} event
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

export default EventForm
